/* global document */
import Splide from '@splidejs/splide';
import '../../../styles/next/blocks/_relationship-carousel.scss';

// acf/next-relationship-carousel: a looping Splide with the active card centred. Card widths are
// in the stylesheet (autoWidth). Splide drops its speed to 0 for reduced motion on its own, and
// hides the cards that are not fully in view (peeks included) from the Tab order and screen
// readers, so keyboards move with the arrows or the arrow keys.
const SELECTOR = '.aqn-relationship-carousel__slider';

// Mouse only: "View relationship" trails the pointer over the active card. Without these
// properties (keyboard focus, touch) the stylesheet puts it in its comp position.
const followCursor = (slider) => {
  slider.addEventListener('pointermove', (event) => {
    const card = event.target.closest('.is-active .aqn-relationship-card');
    if (!card || event.pointerType !== 'mouse') {
      return;
    }
    const rect = card.getBoundingClientRect();
    card.style.setProperty('--aqn-relationship-x', `${event.clientX - rect.left}px`);
    card.style.setProperty('--aqn-relationship-y', `${event.clientY - rect.top}px`);
  });
  slider.addEventListener('pointerout', (event) => {
    const card = event.target.closest('.aqn-relationship-card');
    if (card && !card.contains(event.relatedTarget)) {
      card.style.removeProperty('--aqn-relationship-x');
      card.style.removeProperty('--aqn-relationship-y');
    }
  });
};

const initCarousel = (slider) => {
  // One card has nowhere to go: it stays a static row.
  if (slider.hasAttribute('data-initialized') || slider.querySelectorAll('.splide__slide').length < 2) {
    return;
  }
  slider.setAttribute('data-initialized', 'true');

  const splide = new Splide(slider, {
    type: 'loop',
    focus: 'center',
    autoWidth: true,
    gap: '0.8rem',
    mediaQuery: 'min',
    breakpoints: {
      769: { gap: '1.2rem' },
      1000: { gap: '1.6rem' },
    },
    pagination: false,
    keyboard: 'focused',
    // Splide writes `aria-label` from this option, "undefined" included, so pass the markup's on.
    label: slider.getAttribute('aria-label'),
    i18n: JSON.parse(slider.dataset.i18n || '{}'),
  });

  // The progress thumb sits at the active card's position along the bar.
  splide.on('mounted move', () => slider.style.setProperty('--aqn-relationship-index', splide.index));

  // A peeking card brings itself to the centre instead of following its link.
  splide.on('click', (slide, event) => {
    if (slide.slide.classList.contains('is-active')) {
      return;
    }
    event.preventDefault();
    const active = splide.Components.Slides.getAt(splide.index).slide;
    splide.go(slide.slide.getBoundingClientRect().left < active.getBoundingClientRect().left ? '<' : '>');
  });

  splide.mount();
  // Splide positions with a transform from scroll 0, but the no-JS row can be scrolled: a swipe
  // before this ran, or its snapping, which re-snaps while mount() adds the clones.
  const { track } = splide.Components.Elements;
  track.scrollLeft = 0;
  followCursor(slider);
};

const init = () => document.querySelectorAll(SELECTOR).forEach(initCarousel);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
